import React, { useEffect, useState } from "react";
import { api, fmtINR, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", procedures: [], discount: 0, package_price: 0, min_price: 0, max_price: 0, commission: 0, notes: "" };

export default function Packages() {
  const { isAdmin } = useAuth();
  const [pkgs, setPkgs] = useState([]);
  const [procs, setProcs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => { api.get("/packages").then((r) => setPkgs(r.data)); api.get("/procedures").then((r) => setProcs(r.data)); };
  useEffect(load, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProc = (name) => setForm((f) => ({ ...f, procedures: f.procedures.includes(name) ? f.procedures.filter((n) => n !== name) : [...f.procedures, name] }));

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      const payload = { ...form };
      ["discount", "package_price", "min_price", "max_price", "commission"].forEach((k) => payload[k] = Number(payload[k]) || 0);
      await api.post("/packages", payload); toast.success("Package created"); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/packages/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6" data-testid="packages-page">
      <div className="flex items-center justify-between">
        <div><h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Packages</h2><p className="text-slate-500 mt-1">Bundle procedures into combined offers</p></div>
        {isAdmin && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-package-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus className="h-4 w-4" /> Add Package</Button>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pkgs.map((pk) => (
          <Card key={pk.id} className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm group" data-testid={`pkg-${pk.id}`}>
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-violet-50 dark:bg-violet-950/40 flex items-center justify-center"><Package className="h-5 w-5 text-violet-600" /></div>
              {isAdmin && <Button variant="ghost" size="icon" onClick={() => del(pk.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <h3 className="font-heading font-bold mt-4">{pk.name}</h3>
            <div className="flex flex-wrap gap-1 mt-2">{(pk.procedures || []).map((n) => <Badge key={n} variant="outline" className="text-[10px]">{n}</Badge>)}</div>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Package Price</span><span className="font-semibold text-blue-600">{fmtINR(pk.package_price)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Discount</span><span>{pk.discount}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Range</span><span>{fmtINR(pk.min_price)} – {fmtINR(pk.max_price)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Commission</span><span>{fmtINR(pk.commission)}</span></div>
            </div>
            {pk.notes && <p className="text-xs text-slate-400 mt-3">{pk.notes}</p>}
          </Card>
        ))}
        {pkgs.length === 0 && <p className="text-slate-400">No packages yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle className="font-heading">Add Package</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Package Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pkg-name" placeholder="Mommy Makeover" /></div>
            <div className="space-y-2">
              <Label>Included Procedures</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-3 grid grid-cols-2 gap-2">
                {procs.map((pr) => (
                  <label key={pr.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.procedures.includes(pr.name)} onCheckedChange={() => toggleProc(pr.name)} /> {pr.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[["package_price", "Package Price"], ["discount", "Discount %"], ["min_price", "Min Price"], ["max_price", "Max Price"], ["commission", "Commission"]].map(([k, l]) => (
                <div key={k} className="space-y-2"><Label>{l}</Label><Input type="number" value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
              ))}
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="save-package-btn" className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
